import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
} from "react";

export const MIN_IMAGE_ZOOM = 1;
export const MAX_IMAGE_ZOOM = 4;
export const IMAGE_ZOOM_STEP = 0.05;
export const IMAGE_ZOOM_BUTTON_STEP = 0.25;

const STAGE_IMAGE_SELECTOR = "img[data-file-stage-image]";

type Pan = { x: number; y: number };

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

/** How far the scaled image can move before its edges meet the stage. */
function panLimits(scale: number, container: HTMLElement, img: HTMLImageElement): Pan {
  const cw = container.clientWidth;
  const ch = container.clientHeight;
  const renderedW = img.clientWidth;
  const renderedH = img.clientHeight;

  if (cw <= 0 || ch <= 0 || renderedW <= 0 || renderedH <= 0) {
    return { x: 0, y: 0 };
  }

  const scaledW = renderedW * scale;
  const scaledH = renderedH * scale;
  return {
    x: Math.max(0, (scaledW - cw) / 2),
    y: Math.max(0, (scaledH - ch) / 2),
  };
}

function clampPan(
  pan: Pan,
  scale: number,
  container: HTMLElement,
  img: HTMLImageElement,
): Pan {
  const max = panLimits(scale, container, img);
  return {
    x: clamp(pan.x, -max.x, max.x),
    y: clamp(pan.y, -max.y, max.y),
  };
}

function hasPanRoom(scale: number, container: HTMLElement, img: HTMLImageElement) {
  const max = panLimits(scale, container, img);
  return max.x > 0.5 || max.y > 0.5;
}

function samePan(a: Pan, b: Pan) {
  return a.x === b.x && a.y === b.y;
}

type UseImagePanZoomOptions = {
  containerRef: RefObject<HTMLElement | null>;
  enabled?: boolean;
  resetKey?: unknown;
};

export function useImagePanZoom({
  containerRef,
  enabled = true,
  resetKey,
}: UseImagePanZoomOptions) {
  const [scale, setScaleState] = useState(MIN_IMAGE_ZOOM);
  const [pan, setPan] = useState<Pan>({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [canPan, setCanPan] = useState(false);

  const dragStartRef = useRef<{ panX: number; panY: number; clientX: number; clientY: number } | null>(
    null,
  );
  const activePointerIdRef = useRef<number | null>(null);
  const scaleRef = useRef(MIN_IMAGE_ZOOM);
  scaleRef.current = scale;

  const getImageEl = useCallback(() => {
    const container = containerRef.current;
    if (!container) return null;
    return container.querySelector<HTMLImageElement>(STAGE_IMAGE_SELECTOR);
  }, [containerRef]);

  const reset = useCallback(() => {
    scaleRef.current = MIN_IMAGE_ZOOM;
    setScaleState(MIN_IMAGE_ZOOM);
    setPan({ x: 0, y: 0 });
    setIsDragging(false);
    dragStartRef.current = null;
    activePointerIdRef.current = null;
    const container = containerRef.current;
    const img = getImageEl();
    if (!container || !img) {
      setCanPan(false);
      return;
    }
    setCanPan(hasPanRoom(MIN_IMAGE_ZOOM, container, img));
  }, [containerRef, getImageEl]);

  useEffect(() => {
    reset();
  }, [resetKey, reset]);

  const syncCanPan = useCallback(() => {
    const container = containerRef.current;
    const img = getImageEl();
    if (!container || !img) {
      setCanPan(false);
      return;
    }
    setCanPan(hasPanRoom(scaleRef.current, container, img));
  }, [containerRef, getImageEl]);

  const applyPan = useCallback(
    (nextPan: Pan) => {
      const container = containerRef.current;
      const img = getImageEl();
      if (!container || !img) {
        setPan(nextPan);
        return;
      }
      setPan((prev) => {
        const next = clampPan(nextPan, scale, container, img);
        return samePan(prev, next) ? prev : next;
      });
    },
    [containerRef, getImageEl, scale],
  );

  const setScale = useCallback(
    (next: number) => {
      const clamped = clamp(next, MIN_IMAGE_ZOOM, MAX_IMAGE_ZOOM);
      setScaleState(clamped);
      const container = containerRef.current;
      const img = getImageEl();
      if (container && img) {
        setPan((prev) => {
          const nextPan = clampPan(prev, clamped, container, img);
          return samePan(prev, nextPan) ? prev : nextPan;
        });
        setCanPan(hasPanRoom(clamped, container, img));
        return;
      }
      setCanPan(false);
    },
    [containerRef, getImageEl],
  );

  const reclampPan = useCallback(() => {
    const container = containerRef.current;
    const img = getImageEl();
    if (!container || !img) return;
    setPan((prev) => {
      const next = clampPan(prev, scale, container, img);
      return samePan(prev, next) ? prev : next;
    });
    setCanPan(hasPanRoom(scale, container, img));
  }, [containerRef, getImageEl, scale]);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container || !enabled) {
      setCanPan(false);
      return;
    }

    const ro = new ResizeObserver(reclampPan);
    ro.observe(container);
    const img = getImageEl();
    if (img) ro.observe(img);
    reclampPan();

    return () => ro.disconnect();
  }, [containerRef, enabled, reclampPan, getImageEl, resetKey]);

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (!enabled || event.button !== 0) return;
      if ((event.target as HTMLElement).closest("button, a, input, [data-zoom-control]")) return;

      const container = containerRef.current;
      const img = getImageEl();
      if (!container || !img || !hasPanRoom(scaleRef.current, container, img)) return;

      event.preventDefault();
      event.currentTarget.setPointerCapture(event.pointerId);
      activePointerIdRef.current = event.pointerId;
      dragStartRef.current = {
        panX: pan.x,
        panY: pan.y,
        clientX: event.clientX,
        clientY: event.clientY,
      };
      setIsDragging(true);
    },
    [enabled, pan.x, pan.y, containerRef, getImageEl],
  );

  const onPointerMove = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (!dragStartRef.current || activePointerIdRef.current !== event.pointerId) return;

      const dx = event.clientX - dragStartRef.current.clientX;
      const dy = event.clientY - dragStartRef.current.clientY;
      applyPan({
        x: dragStartRef.current.panX + dx,
        y: dragStartRef.current.panY + dy,
      });
    },
    [applyPan],
  );

  const endDrag = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    if (activePointerIdRef.current !== event.pointerId) return;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    dragStartRef.current = null;
    activePointerIdRef.current = null;
    setIsDragging(false);
  }, []);

  const onPointerUp = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      endDrag(event);
    },
    [endDrag],
  );

  const onPointerCancel = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      endDrag(event);
    },
    [endDrag],
  );

  const onDoubleClick = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (!enabled) return;
      if ((event.target as HTMLElement).closest("button, a, input, [data-zoom-control]")) return;
      reset();
    },
    [enabled, reset],
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !enabled) return;

    const handleWheel = (event: WheelEvent) => {
      if (!event.ctrlKey && !event.metaKey) return;
      event.preventDefault();
      const delta = event.deltaY > 0 ? -IMAGE_ZOOM_STEP : IMAGE_ZOOM_STEP;
      setScale(scaleRef.current + delta);
    };

    container.addEventListener("wheel", handleWheel, { passive: false });
    return () => container.removeEventListener("wheel", handleWheel);
  }, [containerRef, enabled, resetKey, setScale]);

  const transformStyle = useMemo<CSSProperties>(
    () => ({
      transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})`,
      transformOrigin: "center center",
    }),
    [pan.x, pan.y, scale],
  );

  const viewportHandlers = useMemo(
    () => ({
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel,
      onDoubleClick,
    }),
    [onPointerDown, onPointerMove, onPointerUp, onPointerCancel, onDoubleClick],
  );

  const isZoomed = scale > MIN_IMAGE_ZOOM;

  return {
    scale,
    isDragging,
    isZoomed,
    canPan,
    setScale,
    reset,
    transformStyle,
    viewportHandlers,
    syncCanPan,
  };
}
